import React, { ReactNode } from "react"
import { Text, View } from "react-native"

import { fromMarkdown } from "mdast-util-from-markdown"
import { gfmAutolinkLiteralFromMarkdown } from "mdast-util-gfm-autolink-literal"
import { gfmAutolinkLiteral } from "micromark-extension-gfm-autolink-literal"
import styled from "styled-components/native"

import { uniqueId } from "@treefort/lib/unique-id"

import { useAppLinkHelpers } from "../hooks/use-app-link-helpers"
import { getAppLinkFromUrl } from "../lib/app-link"
import { logError } from "../lib/logging"
import { Heading, HeadingLevel } from "./heading"
import TreefortText, { TextStyle } from "./text"

const LinkText = styled.Text`
  color: ${(props) => props.theme.colors.text.accent};
  text-decoration: underline ${(props) => props.theme.colors.text.accent};
`

const PaddedHeading = styled(Heading)`
  margin-bottom: ${(props) => props.theme.spacing.xsmall}px;
`

const PaddedText = styled(TreefortText)`
  margin-bottom: ${(props) => props.theme.spacing.medium}px;
`

const Emphasis = styled.Text`
  font-style: italic;
`

const Strong = styled.Text`
  font-weight: bold;
`

const Blockquote = styled.View`
  border: 0 solid ${(props) => props.theme.colors.gray600};
  border-left-width: ${(props) => props.theme.spacing.xtiny}px;
  padding-left: ${(props) => props.theme.spacing.small}px;
  padding-top: ${(props) => props.theme.spacing.small}px;
  margin-bottom: ${(props) => props.theme.spacing.small}px;
`

const List = styled.View`
  padding-left: ${(props) => props.theme.spacing.small}px;
  padding-bottom: ${(props) => props.theme.spacing.small}px;
  flex-direction: column;
`

const Bullet = styled(TreefortText)`
  padding-right: -${(props) => props.theme.spacing.small}px;
`

const ListItemContent = styled(TreefortText)`
  padding-left: ${(props) => props.theme.spacing.small}px;
  padding-bottom: ${(props) => props.theme.spacing.tiny}px;
`

function ListItem({
  children,
  listItemNumber,
}: {
  children: ReactNode
  listItemNumber?: number
}) {
  return (
    <View style={{ flexDirection: "row" }}>
      <Bullet textStyle="body">
        {listItemNumber ? `${listItemNumber}.` : `\u2022`}
      </Bullet>
      <ListItemContent textStyle="body">{children}</ListItemContent>
    </View>
  )
}

const headingStyleLookups: Record<HeadingLevel, TextStyle> = {
  1: "headingXLarge",
  2: "headingLarge",
  3: "headingMedium",
  4: "headingSmall",
  5: "headingXSmall",
  6: "headingXSmall",
}

const listItemNumberTracker: Record<string, number> = {}

function markdownToReact(
  ast: ReturnType<typeof fromMarkdown>["children"],
  options: {
    onlyPlaintext?: boolean
    getAppLinkProps: ReturnType<typeof useAppLinkHelpers>["getProps"]
    maxAllowedHeadingLevel?: HeadingLevel
    listIdentifier?: string
  },
): React.JSX.Element | string {
  const elements: (React.JSX.Element | string)[] = []

  for (const element of ast) {
    if (options.onlyPlaintext) {
      if ("children" in element) {
        elements.push(markdownToReact(element.children, options))
      } else if ("value" in element) {
        elements.push(element.value)
      }

      continue
    }

    const position = element.position || {
      start: {
        line: Math.floor(Math.random() * 10000),
        column: Math.floor(Math.random() * 10000),
        offset: Math.floor(Math.random() * 10000),
      },
      end: {
        line: Math.floor(Math.random() * 10000),
        column: Math.floor(Math.random() * 10000),
        offset: Math.floor(Math.random() * 10000),
      },
    }

    const elementKey = `${element.type}-${position.start.line}-${position.start.column}-${position.start.offset}-${position.end.line}-${position.end.column}-${position.end.offset}`

    switch (element.type) {
      case "link": {
        const linkText = markdownToReact(element.children, {
          ...options,
          onlyPlaintext: true,
        }) as string

        elements.push(
          <LinkText
            key={elementKey}
            {...options.getAppLinkProps({
              id: "",
              appLink: getAppLinkFromUrl(element.url),
              label: linkText,
            })}
            aria-label={linkText}
          >
            {linkText}
          </LinkText>,
        )
        break
      }
      case "text":
      case "inlineCode":
        elements.push(<Text key={elementKey}>{element.value}</Text>)
        break
      case "heading": {
        const stringContent = markdownToReact(element.children, {
          ...options,
          onlyPlaintext: true,
        }) as string

        const adjustedHeadingLevel = Math.min(
          6,
          element.depth + (options.maxAllowedHeadingLevel || 0),
        ) as HeadingLevel

        elements.push(
          <PaddedHeading
            key={elementKey}
            level={adjustedHeadingLevel}
            textStyle={headingStyleLookups[adjustedHeadingLevel]}
            aria-label={stringContent}
          >
            {markdownToReact(element.children, options)}
          </PaddedHeading>,
        )
        break
      }
      case "paragraph":
        elements.push(
          <PaddedText key={elementKey} textStyle="body">
            {markdownToReact(element.children, options)}
          </PaddedText>,
        )
        break
      case "emphasis":
        elements.push(
          <Emphasis key={elementKey}>
            {markdownToReact(element.children, options)}
          </Emphasis>,
        )
        break
      case "strong":
        elements.push(
          <Strong key={elementKey}>
            {markdownToReact(element.children, options)}
          </Strong>,
        )
        break
      case "blockquote":
        elements.push(
          <Blockquote key={elementKey}>
            {markdownToReact(element.children, options)}
          </Blockquote>,
        )
        break
      case "list": {
        const listIdentifier = uniqueId("list-")
        if (element.ordered) {
          listItemNumberTracker[listIdentifier] = 1
        }

        elements.push(
          <List key={elementKey}>
            {markdownToReact(element.children, {
              ...options,
              listIdentifier: element.ordered ? listIdentifier : undefined,
            })}
          </List>,
        )

        delete listItemNumberTracker[listIdentifier]

        break
      }
      case "listItem": {
        const listItemNumber = options.listIdentifier
          ? listItemNumberTracker[options.listIdentifier]++
          : undefined
        elements.push(
          <ListItem key={elementKey} listItemNumber={listItemNumber}>
            {markdownToReact(element.children, options)}
          </ListItem>,
        )
        break
      }
      case "image":
      case "yaml":
      case "table":
      case "tableCell":
      case "tableRow":
      case "imageReference":
      case "linkReference":
      case "thematicBreak":
      case "footnoteDefinition":
      case "footnoteReference":
      case "html":
      case "break":
      case "code":
      case "delete":
      case "definition":
        // Not supported.
        break
      default:
        element satisfies never

        // Unexpected condition
        logError(
          new Error("Unrecognized markdown element in AST", {
            cause: { element },
          }),
        )
    }
  }

  return options.onlyPlaintext ? elements.join(" ") : <>{elements}</>
}

function NavigationMarkdown({
  children,
  maxAllowedHeadingLevel,
}: {
  children: string
  maxAllowedHeadingLevel?: HeadingLevel
}) {
  const appLinkHelpers = useAppLinkHelpers()

  return (
    <Markdown
      maxAllowedHeadingLevel={maxAllowedHeadingLevel}
      appLinkHelpers={appLinkHelpers}
    >
      {children}
    </Markdown>
  )
}

/**
 * Markdown in, React components out. Simple!
 *
 * @param options
 *   **maxAllowedHeadingLevel:** Specify this to define the highest-ranking
 *   header element allowed in the output. This helps us preserve semantic
 *   structure on pages that include Markdown. For example, if the
 *   Markdown component is included on a page with an H1 and H2, specify
 *   3 as the maxAllowedHeadingLevel and all headings specified in the
 *   Markdown will be demoted by 2 levels - h1 becomes h3, h2 becomes h4, etc.
 * @returns
 */
export function Markdown({
  children,
  maxAllowedHeadingLevel,
  appLinkHelpers,
}: {
  children: string
  maxAllowedHeadingLevel?: HeadingLevel
  appLinkHelpers?: ReturnType<typeof useAppLinkHelpers>
}) {
  if (!appLinkHelpers) {
    // This hack/workaround is present because the appLinkHelpers hook,
    // used to calculate app link destinations, is only available within
    // the NavigationContainer and there's one place we're rendering
    // Markdown outside that context. This allows us to conditionally call
    // the useAppLinkHelpers hook when needed, and the caller can provide an
    // appLinkHelpers instance for cases when we don't have a navigation context.
    return (
      <NavigationMarkdown maxAllowedHeadingLevel={maxAllowedHeadingLevel}>
        {children}
      </NavigationMarkdown>
    )
  }

  const { getProps } = appLinkHelpers

  const ast = fromMarkdown(children, {
    extensions: [gfmAutolinkLiteral()],
    mdastExtensions: [gfmAutolinkLiteralFromMarkdown()],
  })

  const parsed = markdownToReact(ast.children, {
    getAppLinkProps: getProps,
    maxAllowedHeadingLevel,
  })

  return <>{parsed}</>
}
